import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output, QueryList, Renderer2,
  ViewChild, ViewChildren
} from '@angular/core';
import {HttpErrorResponse} from "@angular/common/http";
import {filter, startWith, Subscription, take} from "rxjs";

import {getContentSize} from "../../defines/client.func";
import {SimpleWord} from "../../models/simple-word.model";
import {GlobalEventService} from "../../services/global-event/global-event.service";
import {clickOutsideLinkMixin} from "../../mixin/click-outside.mixin";
import {HanjaService, HanjaWordInfo} from "../../services/hanja/hanja.service";
import {WordDispComponent} from "../word-disp/word-disp.component";

const MAX_WORD_ITEM = 4;  // 한자 단어 팝업에 표시할 최대 단어 수. 서버에서 10개를 보내주므로 10개를 넘어가면 의미가 없다.


@Component({
  selector: 'app-hanja-word-popup',
  templateUrl: './hanja-word-popup.component.html',
  styleUrls: ['./hanja-word-popup.component.scss']
})
export class HanjaWordPopupComponent extends clickOutsideLinkMixin implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('popupContainer') popupContainer?: ElementRef;
  @ViewChildren(WordDispComponent) wordDispComponents!: QueryList<WordDispComponent>;
  @Input() hanja?: string;
  @Input() hangul?: string;    // 음 (1글자, 예: "국")
  @Input() firstHun?: string;  // 대표 훈 (예: "나라")
  @Output() close = new EventEmitter<string | null>();
  @Output() viewInit = new EventEmitter<HTMLElement>();

  protected wordColumnMinWidth: number | null = null;
  protected errorTxt: string | null = null;

  protected popupTop: number = 0;

  private load$: Subscription | null = null;
  protected hanjaWordInfo: HanjaWordInfo | null = null;

  protected get listWord(): Array<SimpleWord> | null {
    return this.hanjaWordInfo?.words.slice(0, MAX_WORD_ITEM) ?? null;
  }

  private get totalWordCount(): number | null {
    return this.hanjaWordInfo?.word_total_count ?? null;
  }

  protected get IsLoading(): boolean {
    return this.load$ !== null;
  }

  constructor(
    public override elementRef: ElementRef,
    public override renderer: Renderer2,
    public override globalEventSvc: GlobalEventService,
    private hanjaSvc: HanjaService,
  ) {
    super(elementRef, renderer, globalEventSvc);
  }

  ngOnInit() {
    // mixin을 이용해서 팝업창 외부를 클릭했는지 체크한다.
    this.coInit(() => this.close.emit(null));

    if (!this.hanja) {
      console.error('HanjaWordPopupComponent: hanja input is required');
      this.errorTxt = '표시할 한자가 지정되지 않았습니다.';
      return;
    }

    this.load$ = this.hanjaSvc.GetHanjaWordInfo(this.hanja, this.hangul).subscribe({
      next: (hanjaWordInfo: HanjaWordInfo) => {
        console.warn('HanjaWordPopupComponent.ngOnInit: head', hanjaWordInfo);
        this.hanjaWordInfo = hanjaWordInfo;
      },
      error: (error) => {
        this.load$ = null;  // 로딩 상태 해제
        if (error instanceof HttpErrorResponse && error.status === 404) {
          this.errorTxt = '문자 정보를 찾을 수 없습니다.';
        } else {
          console.error('HanjaWordPopupComponent.ngOnInit: error', error);
          this.errorTxt = '정보를 불러오는 중 오류가 발생했습니다.';
        }
      },
      complete: () => {
        this.load$ = null;
      }
    });
  }

  ngAfterViewInit() {
    this.viewInit.emit(this.elementRef.nativeElement.firstChild);

    // position이 설정된 부모 요소 찾기
    const positionedParent = this.elementRef.nativeElement.offsetParent;

    const {height} = getContentSize(positionedParent);
    const popupTop = height + 5;
    this.popupTop = popupTop;

    // word-disp 컴포넌트들이 렌더된 후 가장 넓은 폭을 기준으로 min-width 적용
    this.wordDispComponents.changes.pipe(
      startWith(this.wordDispComponents),
      filter((comps: QueryList<WordDispComponent>) => comps.length > 0),
      take(1)
    ).subscribe(comps => {
      setTimeout(() => {
        const maxWidth = Math.max(...comps.map((c: WordDispComponent) => c.width));
        if (maxWidth > 0) {
          this.wordColumnMinWidth = maxWidth;
        }
      });
    });
  }

  ngOnDestroy() {
    // 기존 mixin 정리
    this.coDestroy();

    // 구독 해제 처리
    if (this.load$) {
      this.load$.unsubscribe();
      this.load$ = null;
    }
  }

  protected getMoreTxt(): string | null {
    let moreTxt: string | null = null;
    const leftCount = (this.totalWordCount ?? 0) - (this.listWord?.length ?? 0);
    moreTxt = leftCount ? `${leftCount}개의 단어가 더 있습니다.` : null;
    return moreTxt;
  }

  /**
   * 상위 요소로 이벤트 전파를 막는다.
   * @param event
   */
  onVoid(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.globalEventSvc.Click(event);
  }

  onCloseMore($event: MouseEvent) {
    $event.stopPropagation();
    $event.preventDefault();
    this.close.emit(this.hanja);
  }

  onClose($event: MouseEvent) {
    $event.stopPropagation();
    $event.preventDefault();
    this.close.emit(null);
  }

  protected GetMeaning(word: SimpleWord): string {
    return word.GetBriefMeaning() ?? '';
  }
}
